import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { MainComponent } from '@modules/main/main.component';
import { BlankComponent } from '@pages/blank/blank.component';
import { LoginComponent } from '@modules/login/login.component';
import { ProfileComponent } from '@pages/profile/profile.component';
import { RegisterComponent } from '@modules/register/register.component';
import { DashboardComponent } from '@pages/dashboard/dashboard.component';
import { DashboardRevampComponent } from '@pages/dashboard-revamp/dashboard-revamp.component';
import { AuthGuard } from '@guards/auth.guard';
import { NonAuthGuard } from '@guards/non-auth.guard';
import { ForgotPasswordComponent } from '@modules/forgot-password/forgot-password.component';
import { RecoverPasswordComponent } from '@modules/recover-password/recover-password.component';
import { PenalComponent } from '@modules/penal/penal.component';
import { ResetpasswordComponent } from '@modules/resetpassword/resetpassword.component';
import { PrivacyPolicyComponent } from '@pages/privacy-policy/privacy-policy.component';

// Plant Management Components
import { PlantsComponent } from '@pages/plants/plants.component';
import { PlantDetailComponent } from '@pages/plant-detail/plant-detail.component';
import { PlantProfileListComponent } from '@pages/plant-profile-list/plant-profile-list.component';
import { PlantProfileComponent } from '@pages/plant-profile/plant-profile.component';
import { PlantTariffComponent } from '@pages/plant-tariff/plant-tariff.component';
import { PlantComparisonComponent } from '@pages/plant-comparison/plant-comparison.component';

// Inverter Components
import { InverterPvGraphComponent } from '@components/inverter-pv-graph/inverter-pv-graph.component';
import { InverterComparisonComponent } from '@pages/inverter-comparison/inverter-comparison.component';

// Location Management Components
import { RegionAndCityComponent } from '@pages/region-and-city/region-and-city.component';
import { ClusterComponent } from '@pages/cluster/cluster.component';
import { WeatherStationComponent } from '@pages/weather-station/weather-station.component';

// Analysis & Reports Components
import { AnalysisHeatMapComponent } from '@pages/analysis-heat-map/analysis-heat-map.component';
import { DailyReportComponent } from '@pages/daily-report/daily-report.component';
import { MonthlyReportComponent } from '@pages/monthly-report/monthly-report.component';

// Tariff Management Components
import { TariffTypeComponent } from '@pages/tariff-type/tariff-type.component';
import { TariffCompanyComponent } from '@pages/tariff-company/tariff-company.component';
import { TariffRatesComponent } from '@pages/tariff-rates/tariff-rates.component';

// Help Desk Components
import { HelpDeskDashboardComponent } from '@pages/help-desk-dashboard/help-desk-dashboard.component';
import { TicketListComponent } from '@pages/ticket-list/ticket-list.component';
import { AddTicketComponent } from '@pages/add-ticket/add-ticket.component';
import { PreorityComponent } from '@pages/preority/preority.component';
import { SourceComponent } from '@pages/source/source.component';
import { CategoryComponent } from '@pages/category/category.component';
import { SubCategoryComponent } from '@pages/sub-category/sub-category.component';

// Alarm Components
import { AlarmComponent } from '@pages/alarm/alarm.component';
import { HistoricalAlarmsComponent } from '@pages/historical-alarms/historical-alarms.component';

// User Management Components
import { UserListComponent } from '@pages/user-list/user-list.component';
import { InactiveUserListComponent } from '@pages/inactive-user-list/inactive-user-list.component';

// Communication Components
import { EmailComponent } from '@pages/email/email.component';

// Other Components
import { MainMenuComponent } from '@pages/main-menu/main-menu.component';
import { SubMenuComponent } from '@pages/main-menu/sub-menu/sub-menu.component';
import { CommingSoonComponent } from '@pages/comming-soon/comming-soon.component';
import { UserTypeComponent } from '@pages/user-type/user-type.component';
import { UserRoleComponent } from '@pages/user-role/user-role.component';
import { LogsUserManagementComponent } from '@pages/logs-user-management/logs-user-management.component';
import { UserPermissionsComponent } from '@pages/user-permissions/user-permissions.component';
import { TicketConfigurationComponent } from '@pages/ticket-configuration/ticket-configuration.component';
import { TicketPriorityComponent } from '@pages/ticket-priority/ticket-priority.component';
import { TicketsComponent } from '@pages/tickets/tickets.component';
import { TicketDetailComponent } from '@pages/ticket-detail/ticket-detail.component';
import { FsrComponent } from '@pages/fsr/fsr.component';
import { FsrDetailComponent } from '@pages/fsr-detail/fsr-detail.component';
import { FsrConfigurationComponent } from '@pages/fsr-configuration/fsr-configuration.component';
import { ConfigurationsComponent } from '@pages/configurations/configurations.component';

const routes: Routes = [
  // ==================== Public Routes ====================
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [NonAuthGuard]
  },
  {
    path: 'register',
    component: RegisterComponent,
    canActivate: [NonAuthGuard]
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
    canActivate: [NonAuthGuard]
  },
  {
    path: 'recover-password',
    component: RecoverPasswordComponent,
    canActivate: [NonAuthGuard]
  },
  {
    path: 'login/resetPassword',
    component: ResetpasswordComponent,
    canActivate: [NonAuthGuard]
  },
  {
    path: 'panel',
    component: PenalComponent,
    canActivate: [NonAuthGuard]
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyComponent
  },
  {
    path: 'weather-station',
    component: WeatherStationComponent
  },

  // ==================== Protected Routes ====================
  {
    path: '',
    component: MainComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    children: [
      // Dashboard (Home)
      {
        path: '',
        component: DashboardRevampComponent,
        // component: DashboardComponent,
        data: { title: 'Dashboard' }
      },
      {
        path: 'dashboard',
        redirectTo: '',
        pathMatch: 'full'
      },
      // {
      //   path: 'dashboard-revamp',
      //   component: DashboardRevampComponent,
      //   data: { title: 'Dashboard (Revamp Preview)' }
      // },

      // Profile
      {
        path: 'profile',
        component: ProfileComponent,
        data: { title: 'Profile' }
      },

      // ==================== Configurations (merged tab page) ====================
      {
        path: 'configurations',
        component: ConfigurationsComponent,
        data: { title: 'Configurations' }
      },

      // ==================== Plant Management Module ====================
      {
        path: 'plant-management',
        data: { title: 'Plant Management' },
        children: [
          {
            path: 'plants',
            component: PlantsComponent,
            data: { title: 'Plants' }
          },
          {
            // parentCrumbs: this page is reached from the plants list but is
            // a sibling of it in the route tree, so the trail has to name
            // that step explicitly. titleFrom swaps the static title for the
            // plant's own name, which reads better as the last crumb.
            path: 'plant-detail',
            component: PlantDetailComponent,
            data: { title: 'Plant Details', parentCrumbs: ['Plants'], titleFrom: 'selectedPlantName' }
          },
          {
            path: 'plant-detail/:id',
            component: PlantDetailComponent,
            data: { title: 'Plant Details', parentCrumbs: ['Plants'], titleFrom: 'selectedPlantName' }
          },
          {
            path: 'plant-profile',
            component: PlantProfileListComponent,
            data: { title: 'Plant Profile' }
          },
          {
            // No tab in the URL → open Devices.
            path: 'plant-profile/:id',
            redirectTo: 'plant-profile/:id/devices',
            pathMatch: 'full'
          },
          {
            // Reached from the plant profile list; ends in the plant's name.
            // :tab (devices | roi | panels) keeps the open tab across refreshes.
            path: 'plant-profile/:id/:tab',
            component: PlantProfileComponent,
            data: { title: 'Plant Profile', parentCrumbs: ['Plant Profile'], titleFrom: 'selectedPlantName' }
          },
          {
            path: 'region-and-city',
            component: RegionAndCityComponent,
            data: { title: 'Region & City' }
          },
          {
            // Same as plant-detail above — reached from the plants list.
            path: 'cluster',
            component: ClusterComponent,
            data: { title: 'Cluster', parentCrumbs: ['Plants'] }
          },
          {
            path: 'cluster/:id',
            component: ClusterComponent,
            data: { title: 'Cluster', parentCrumbs: ['Plants'] }
          }
        ]
      },

      // ==================== Inverter Management Module ====================
      // {
      //   path: 'inverter-management',
      //   data: { title: 'Inverter Management' },
      //   children: [
      //     {
      //       path: 'inverter-pv-graph',
      //       component: InverterPvGraphComponent,
      //       data: { title: 'Inverter PV Graph' }
      //     },
      //   ]
      // },

      // ==================== Analysis & Reports Module ====================
      {
        path: 'analysis',
        data: { title: 'Analysis & Reports' },
        children: [
          {
            path: 'heat-map',
            component: AnalysisHeatMapComponent,
            data: { title: 'Heat Map Analysis' }
          },
          {
            path: 'daily-report',
            component: DailyReportComponent,
            data: { title: 'Daily Report' }
          },
          {
            path: 'monthly-report',
            component: MonthlyReportComponent,
            data: { title: 'Monthly Report' }
          },
          {
            path: 'plant-comparison',
            component: PlantComparisonComponent,
            data: { title: 'Plant Comparison' }
          },
          {
            path: 'inverter-comparison',
            component: InverterComparisonComponent,
            data: { title: 'Inverter Comparison' }
          }
        ]
      },

      // ==================== Tariff Management Module ====================
      {
        path: 'tariff-management',
        data: { title: 'Tariff Management' },
        children: [
          {
            path: 'tariff-type',
            component: TariffTypeComponent,
            data: { title: 'Tariff Type' }
          },
          {
            path: 'tariff-company',
            component: TariffCompanyComponent,
            data: { title: 'Tariff Company' }
          },
          {
            path: 'tariff-rates',
            component: TariffRatesComponent,
            data: { title: 'Tariff Rates' }
          },
          {
            path: 'plant-tariff-association',
            component: PlantTariffComponent,
            data: { title: 'Plant Tariff Association' }
          }
        ]
      },


      // ==================== Ticket Management Module ====================
      // {
      //   path: 'ticket-management',
      //   data: { title: 'Ticket Management' },
      //   children: [
      //     {
      //       path: 'configuration',
      //       component: TicketConfigurationComponent,
      //       data: { title: 'Configuration' }
      //     },
      //     {
      //       path: 'priority',
      //       component: TicketPriorityComponent,
      //       data: { title: 'Priority' }
      //     },
      //     {
      //       path: 'tickets',
      //       component: TicketsComponent,
      //       data: { title: 'Tickets' }
      //     },
      //     {
      //       path: 'tickets/:id',
      //       component: TicketDetailComponent,
      //       data: { title: 'Ticket Details' }
      //     }
      //   ]
      // },

      // ==================== Help Desk Module ====================
      {
        path: 'help-desk',
        data: { title: 'Help Desk' },
        children: [
          // {
          //   path: 'dashboard',
          //   component: HelpDeskDashboardComponent,
          //   data: { title: 'Help Desk Dashboard' }
          // },
          // {
          //   path: 'tickets',
          //   component: TicketListComponent,
          //   data: { title: 'Tickets' }
          // },
          // {
          //   path: 'add-ticket',
          //   component: AddTicketComponent,
          //   data: { title: 'Add Ticket' }
          // },
          // {
          //   path: 'edit-ticket/:id',
          //   component: AddTicketComponent,
          //   data: { title: 'Edit Ticket' }
          // },
          // {
          //   path: 'priority',
          //   component: PreorityComponent,
          //   data: { title: 'Priority' }
          // },
          {
            path: 'source',
            component: SourceComponent,
            data: { title: 'Source' }
          },
          {
            path: 'category',
            component: CategoryComponent,
            data: { title: 'Category' }
          },
          {
            path: 'sub-category',
            component: SubCategoryComponent,
            data: { title: 'Sub Category' }
          },
          {
            path: 'configuration',
            component: TicketConfigurationComponent,
            data: { title: 'Configuration' }
          },
          {
            path: 'priority',
            component: TicketPriorityComponent,
            data: { title: 'Priority' }
          },
          {
            path: 'tickets',
            component: TicketsComponent,
            data: { title: 'Tickets' }
          },
          {
            path: 'tickets/:id',
            component: TicketDetailComponent,
            data: { title: 'Ticket Details' }
          },
          {
            path: 'fsr-configuration',
            component: FsrConfigurationComponent,
            data: { title: 'FSR Configuration' }
          },
          {
            path: 'fsr',
            component: FsrComponent,
            data: { title: 'FSR' }
          },
          {
            path: 'fsr/:id',
            component: FsrDetailComponent,
            data: { title: 'FSR Details' }
          }
        ]
      },

      // ==================== Alarm Management Module ====================
      {
        path: 'alarms',
        data: { title: 'Alarm Management' },
        children: [
          {
            path: 'active',
            component: AlarmComponent,
            data: { title: 'Active Alarms' }
          },
          {
            path: 'historical',
            component: HistoricalAlarmsComponent,
            data: { title: 'Historical Alarms' }
          }
        ]
      },

      // ==================== User Management Module ====================
      {
        path: 'user-management',
        data: { title: 'User Management' },
        children: [
          {
            // Active
            path: 'users',
            component: UserListComponent,
            data: { title: 'Users' }
          },
          // {
          //   path: 'inactive-users',
          //   component: InactiveUserListComponent,
          //   data: { title: 'Inactive Users' }
          // },
          {
            path: 'users-type',
            component: UserTypeComponent,
            data: { title: 'Users Type' }
          },
          {
            path: 'users-role',
            component: UserRoleComponent,
            data: { title: 'Users Role' }
          },
          // {
          //   path: 'users-permissions',
          //   component: UserPermissionsComponent,
          //   data: { title: 'Users Permissions' }
          // },
          {
            path: 'audit-trail',
            component: LogsUserManagementComponent,
            data: { title: 'Audit Trail' }
          },
        ]
      },

      // ==================== Communication Module ====================
      // {
      //   path: 'communication',
      //   data: { title: 'Communication' },
      //   children: [
      //     {
      //       path: 'email',
      //       component: EmailComponent,
      //       data: { title: 'Email' }
      //     }
      //   ]
      // },

      // ==================== Legacy/Temporary Routes ====================
      // These can be removed or reorganized as needed
      {
        path: 'sub-menu-1',
        component: SubMenuComponent
      },
      {
        path: 'sub-menu-2',
        component: BlankComponent
      },
      {
        path: 'coming-soon',
        component: CommingSoonComponent
      },

      // ==================== Backward Compatibility Routes ====================
      // Keep old routes working while transitioning
      {
        path: 'plants',
        redirectTo: 'plant-management/plants',
        pathMatch: 'full'
      },
      {
        path: 'plant-detail',
        redirectTo: 'plant-management/plant-detail',
        pathMatch: 'full'
      },
      {
        path: 'region-and-city',
        redirectTo: 'location-management/region-and-city',
        pathMatch: 'full'
      },
      {
        path: 'cluster',
        redirectTo: 'location-management/cluster',
        pathMatch: 'full'
      },
      {
        path: 'help-desk-dashboard',
        redirectTo: 'help-desk/dashboard',
        pathMatch: 'full'
      },
      {
        path: 'ticket-list',
        redirectTo: 'help-desk/tickets',
        pathMatch: 'full'
      },
      {
        path: 'add-ticket',
        redirectTo: 'help-desk/add-ticket',
        pathMatch: 'full'
      },
      {
        path: 'priority',
        redirectTo: 'help-desk/priority',
        pathMatch: 'full'
      },
      {
        path: 'source',
        redirectTo: 'help-desk/source',
        pathMatch: 'full'
      },
      {
        path: 'category',
        redirectTo: 'help-desk/category',
        pathMatch: 'full'
      },
      {
        path: 'sub-category',
        redirectTo: 'help-desk/sub-category',
        pathMatch: 'full'
      },
      {
        path: 'user',
        redirectTo: 'user-management/active-users',
        pathMatch: 'full'
      },
      {
        path: 'inactive-user',
        redirectTo: 'user-management/inactive-users',
        pathMatch: 'full'
      },
      {
        path: 'alarm',
        redirectTo: 'alarms/active',
        pathMatch: 'full'
      },
      {
        path: 'historical-alarms',
        redirectTo: 'alarms/historical',
        pathMatch: 'full'
      },
      {
        path: 'email',
        redirectTo: 'communication/email',
        pathMatch: 'full'
      },
      {
        path: 'tariff-type',
        redirectTo: 'tariff-management/tariff-type',
        pathMatch: 'full'
      },
      {
        path: 'tariff-company',
        redirectTo: 'tariff-management/tariff-company',
        pathMatch: 'full'
      },
      {
        path: 'tariff-rates',
        redirectTo: 'tariff-management/tariff-rates',
        pathMatch: 'full'
      },
      {
        path: 'plant-association',
        redirectTo: 'plant-management/plant-tariff-association',
        pathMatch: 'full'
      },
      {
        path: 'analysis-heat-map',
        redirectTo: 'analysis/heat-map',
        pathMatch: 'full'
      },
      {
        path: 'analysis-daily-report',
        redirectTo: 'analysis/daily-report',
        pathMatch: 'full'
      },
      {
        path: 'analysis-monthly-report',
        redirectTo: 'analysis/monthly-report',
        pathMatch: 'full'
      },
      {
        path: 'plant-comparison',
        redirectTo: 'plant-management/plant-comparison',
        pathMatch: 'full'
      },
      {
        path: 'inverter-comparison',
        redirectTo: 'inverter-management/inverter-comparison',
        pathMatch: 'full'
      },
      {
        path: 'inverter-pv-graph',
        redirectTo: 'inverter-management/inverter-pv-graph',
        pathMatch: 'full'
      }
    ]
  },

  // Wildcard route - must be last
  {
    path: '**',
    redirectTo: ''
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      relativeLinkResolution: 'legacy',
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled'
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }

export const routingComponent = [
  LoginComponent,
  DashboardComponent,
  PlantsComponent,
  CommingSoonComponent
];